// ds-typo-ignore-file — WIP page, typography tokens to be normalized before ship
"use client"

import { blobMediaUrl } from "@/lib/blob-url"
import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import dynamic from "next/dynamic"
import { useTranslations } from "@/lib/intl/use-translations"
import { m } from "framer-motion"
import { expoOut } from "@/lib/motion"
import Link from "next/link"
import { BookingCta } from "@/components/ui/booking-cta"
import {
  HeartCheckPulsingIcon,
  InsuranceCoverageCta,
} from "@/components/marketing/insurance-coverage-cta"
import { NewMembershipPill } from "@/components/ui/new-membership-pill"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { isComingSoon } from "@/i18n/config"
import type { ServiceCardData } from "@/components/ui/services-carousel"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import {
  ChevronDownIcon,
  ChevronDownSmIcon,
  ExerciseIcon,
  FollowUpIcon,
  GreenCheck,
  ImpactAreaIcon,
  LifestyleIcon,
  NutritionIcon,
  Report2Icon,
  SupplementsIcon,
  SwissShieldIcon,
} from "@/components/ui/icons"
import { cn } from "@/lib/utils"
import { useLocalePath, useLocalePrefix } from "@/lib/use-localized-navigation"
import { sanitizeHtml } from "@/lib/sanitize"
import type {
  CMSBiomarkerListItem,
  Testimonial,
  CmsServiceConfig,
} from "@/lib/cms"
import { applyServicesConfig } from "@/lib/services-overlay"
import { fetchPrices, type PriceData } from "@/lib/pricing"
import {
  memberPriceChf,
  memberPriceLabelChf,
} from "@/lib/pricing/member-prices"
import { formatNumber } from "@/lib/format"
import { membershipKeys as mk } from "@/i18n/membership-keys"
import { IncludedFigmaGrid } from "../../_components/included-grid"
import { useCountry } from "@/components/layout/country-provider"
import { CountryGate } from "@/components/layout/country-gate"

/* ─── Below-fold sections — lazy-loaded JS chunks (SSR HTML still rendered) ─── */
const LogoMarquee = dynamic(() =>
  import("@/components/ui/logo-marquee").then((m) => ({
    default: m.LogoMarquee,
  }))
)
const ServicesCarousel = dynamic(() =>
  import("@/components/ui/services-carousel").then((m) => ({
    default: m.ServicesCarousel,
  }))
)
const CarouselNav = dynamic(() =>
  import("@/components/ui/carousel-nav").then((m) => ({
    default: m.CarouselNav,
  }))
)
const BiomarkerDiseaseExplorer = dynamic(() =>
  import("@/components/ui/biomarker-disease-explorer").then((m) => ({
    default: m.BiomarkerDiseaseExplorer,
  }))
)
const TestimonialsSection = dynamic(() =>
  import("@/app/(frontend)/[country]/[locale]/_components/testimonials-section").then(
    (m) => ({ default: m.TestimonialsSection })
  )
)
const MembershipYearLazy = dynamic(() =>
  import("./membership-year").then((m) => ({
    default: m.MembershipYear,
  }))
)

/* ────────────────────────────────────────────
   Data
   ────────────────────────────────────────────
   PLACEHOLDER ASSETS — to be replaced:
   - Hero video: reusing /videos/about-hero.mp4 until membership-specific lifestyle reel exists
   - Per-USP illustrations: reusing /images/homepage/service-*.jpeg
   - Per-step timeline images: reusing /images/homepage/service-*.jpeg
   ──────────────────────────────────────────── */

const HERO_KPIS = [
  { valueKey: mk.heroKpi1Value, labelKey: mk.heroKpi1Label },
  { valueKey: mk.heroKpi2Value, labelKey: mk.heroKpi2Label },
  { valueKey: mk.heroKpi3Value, labelKey: mk.heroKpi3Label },
] as const

/* Pricing — with helper text + comparison row */
const FREQUENCY_OPTIONS = [
  {
    nameKey: mk.frequencyBaselineName,
    price: 299,
    testsKey: mk.frequencyBaselineTests,
    descKey: mk.frequencyBaselineDesc,
  },
  {
    nameKey: mk.frequency2xName,
    price: 499,
    testsKey: mk.frequency2xTests,
    descKey: mk.frequency2xDesc,
  },
  {
    nameKey: mk.frequency4xName,
    price: 899,
    testsKey: mk.frequency4xTests,
    descKey: mk.frequency4xDesc,
  },
]

/* Member pricing catalog — the access story, not just a discount table.
   `regular` / `member` / `save` are literal CHF fallbacks used on first paint
   and whenever the live Stripe feed lacks a figure for the row. `priceKey` is
   the price-map key the live regular/member prices (and the computed save
   delta) resolve from; omitted for rows with no live product (e.g. the
   follow-up blood test, which isn't in the get-products feed). */
const MEMBER_PRICING_ITEMS: {
  productKey: string
  priceKey?: string
  regular: string
  member: string
  save: string
}[] = [
  {
    productKey: mk.memberPricingItemMriCore,
    priceKey: "core",
    regular: "1’990",
    member: "1’890",
    save: "100",
  },
  {
    productKey: mk.memberPricingItemMriAdvanced,
    priceKey: "advanced",
    regular: "2’490",
    member: "2’290",
    save: "200",
  },
  {
    productKey: mk.memberPricingItemMriPro,
    priceKey: "pro",
    regular: "3’549",
    member: "3’290",
    save: "259",
  },
  {
    productKey: mk.memberPricingItemFollowupBlood,
    regular: "250",
    member: "239",
    save: "11",
  },
  {
    productKey: mk.memberPricingItemAdvancedBlood,
    priceKey: "blood",
    regular: "299",
    member: "279",
    save: "20",
  },
  {
    productKey: mk.memberPricingItemHormone,
    priceKey: "addonHormoneWomen",
    regular: "149",
    member: "119",
    save: "30",
  },
  {
    productKey: mk.memberPricingItemOmega,
    priceKey: "addonOmega",
    regular: "129",
    member: "119",
    save: "10",
  },
  {
    productKey: mk.memberPricingItemBVitamins,
    priceKey: "addonBVitamins",
    regular: "49",
    member: "39",
    save: "10",
  },
  {
    productKey: mk.memberPricingItemBodyAi,
    priceKey: "addonBodyComposition",
    regular: "100",
    member: "89",
    save: "11",
  },
  {
    productKey: mk.memberPricingItemDexa,
    priceKey: "addonDEXA",
    regular: "135",
    member: "119",
    save: "16",
  },
  {
    productKey: mk.memberPricingItemLpa,
    regular: "29",
    member: "25",
    save: "4",
  },
  {
    productKey: mk.memberPricingItemPsa,
    regular: "19",
    member: "15",
    save: "4",
  },
  {
    productKey: mk.memberPricingItemCortisol,
    regular: "29",
    member: "25",
    save: "4",
  },
]

/* ── Live member-pricing resolution ──
   Prefer the live Stripe feed (`prices`); fall back to the row's literal CHF
   figure whenever the feed lacks a value. The save delta is ALWAYS computed
   (regular − member) rather than trusted as a literal, and only when both the
   regular and member figures come from the feed — otherwise the literal save
   stands so we never mix sources. */

/** Formatted save delta (bare number, e.g. "100"), or null when the feed lacks
 *  a live regular+member pair for `key`. */
function feedSaveAmount(
  prices: PriceData | null | undefined,
  key: string | undefined,
  country: string
): string | null {
  if (!key || !prices) return null
  const regularRaw = Number(prices.prices[key])
  const memberRaw = memberPriceChf(prices.prices, key)
  if (!Number.isFinite(regularRaw) || memberRaw == null) return null
  return formatNumber(regularRaw - memberRaw, country)
}

/** Resolve a catalog row's display strings (each already currency-prefixed). */
function resolveMemberRow(
  item: (typeof MEMBER_PRICING_ITEMS)[number],
  prices: PriceData | null | undefined,
  country: string
): { regular: string; member: string; save: string } {
  const key = item.priceKey
  const regular = (key && prices?.fullPrices[key]) || `CHF ${item.regular}`
  const member =
    (key && prices ? memberPriceLabelChf(prices.fullPrices, key) : null) ??
    `CHF ${item.member}`
  const save = `−CHF ${feedSaveAmount(prices, key, country) ?? item.save}`
  return { regular, member, save }
}

/* Doctor names are proper nouns and stay un-translated. Their academic
   titles are in `_membership` translations under expert*Title. */
const EXPERTS = [
  {
    name: "Dr. med. Anna Erat, PhD",
    titleKey: mk.expert4Title,
    photo: blobMediaUrl("/images/doctors/erat.jpeg"),
  },
  {
    name: "Prof. Dr. med. Olivio Donati",
    titleKey: mk.expert2Title,
    photo: blobMediaUrl("/images/doctors/donati.jpeg"),
  },
  {
    name: "Prof. Dr. med. Michael Fischer",
    titleKey: mk.expert3Title,
    photo: blobMediaUrl("/images/about/michael-fischer.jpg"),
  },
  {
    name: "Prof. Dr. Benedikt Wiestler",
    titleKey: mk.expert5Title,
    photo: blobMediaUrl("/images/doctors/wiestler.jpeg"),
  },
  {
    name: "Dr. Jacob J. Visser",
    titleKey: mk.expert6Title,
    photo: blobMediaUrl("/images/doctors/visser.jpg"),
  },
  {
    name: "Dr. Sven Jaeschke",
    titleKey: mk.expert1Title,
    photo: blobMediaUrl("/images/about/sven-jaeschke.jpg"),
  },
]

/* ────────────────────────────────────────────
   Shared UI
   ──────────────────────────────────────────── */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="type-label-md mb-3 text-neutral-500">{children}</p>
}

/* Section header — left-aligned title on the left, description on the right.
   Used across What's Included, Cadence, Blood test, FAQ. */
function SectionHeader({
  label,
  title,
  description,
  rightSlot,
}: {
  label?: string
  title: React.ReactNode
  description?: React.ReactNode
  rightSlot?: React.ReactNode
}) {
  return (
    <div className="mb-12 grid grid-cols-1 items-end gap-6 md:grid-cols-[1.1fr_1fr] md:gap-10">
      <div>
        {label && <SectionLabel>{label}</SectionLabel>}
        <h2 className="text-[30px] leading-[38px] font-normal tracking-[-0.25px] text-neutral-900 md:text-[48px] md:leading-[56px] md:tracking-[-0.5px]">
          {title}
        </h2>
      </div>
      {(description || rightSlot) && (
        <div className="flex items-end gap-4">
          {description && (
            <p className="type-body-lg flex-1 text-neutral-600">
              {description}
            </p>
          )}
          {rightSlot}
        </div>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────
   1. Hero — platform reframe, video bg, primary CTA + insurance CTA
   ──────────────────────────────────────────── */

function Hero({ heroPoster }: { heroPoster: React.ReactNode }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const ut = useTranslations("ui")
  const lp = useLocalePath()
  const videoRef = useRef<HTMLVideoElement>(null)
  const { code: country, currency } = useCountry()
  // Membership is an expansion product: in a `checkup` market the hero CTAs
  // open the waitlist modal (via `forMembership` → isBookingOpen) and we flag
  // it with a "coming soon" pill.
  const membershipComingSoon = isComingSoon(country, "membership")
  // Hero KPI 2 is the "from <price>/year" stat — drop it (and let the strip
  // fall back to 2 columns) on markets where prices are withheld.
  const heroKpis =
    currency === "CHF" ? HERO_KPIS : HERO_KPIS.filter((_, i) => i !== 1)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Respect Save-Data: skip the video entirely for users on data-savings
    // mode (or when the browser exposes one). They keep the poster.
    const conn = (navigator as { connection?: { saveData?: boolean } })
      .connection
    if (conn?.saveData) return

    const schedule =
      "requestIdleCallback" in window
        ? (cb: () => void) => window.requestIdleCallback(cb, { timeout: 2000 })
        : (cb: () => void) => window.setTimeout(cb, 1000)

    schedule(() => {
      // Lazy-attach the source(s) after the poster has painted. The Image
      // above (priority + fetchPriority high) is the LCP element; we don't
      // want video metadata fetching to compete for bandwidth before LCP fires.
      //
      // Mobile gets a portrait-cropped 540×960 H.264 (~810 KB) — the visible
      // region under object-cover on a phone viewport, encoded once instead of
      // shipping the full 1280×720 frame and discarding the sides at render.
      // Desktop gets AV1 first (~1.3 MB) with the original H.264 (~1.7 MB)
      // as fallback for browsers that can't decode AV1 (Safari ≤16, older
      // Android). Breakpoint matches the section's md:rounded-[32px] handoff.
      const isMobile = window.matchMedia("(max-width: 767px)").matches

      const append = (src: string, type: string) => {
        const source = document.createElement("source")
        source.src = blobMediaUrl(src)
        source.type = type
        video.appendChild(source)
      }

      if (isMobile) {
        append("/videos/membership-hero-mobile.mp4", "video/mp4")
      } else {
        append(
          "/videos/membership-hero.av1.mp4",
          'video/mp4; codecs="av01.0.05M.08"'
        )
        append("/videos/membership-hero.mp4", "video/mp4")
      }
      video.load()
    })
  }, [])

  return (
    <section className="bg-background md:px-4 md:pt-2 md:pb-4">
      {/* Mobile layout differs from desktop in two ways (AHD-10495), both to
          stop the centred content colliding with the KPI strip on a short
          viewport (360×610 — a Galaxy S25 with browser chrome showing):
            1. `min-h` instead of `h-svh`, so the card grows when the content
               is taller than the viewport rather than overflowing it.
            2. a column layout with the KPI strip in normal flow, so the
               space it needs is measured rather than guessed. The old
               `max-md:pb-[88px]` reserve under-shot the strip's real height
               (101px EN / 112px FR / 116px DE, since the labels wrap at
               360px) and the insurance CTA landed on top of it.
          Desktop keeps the absolutely-pinned strip and its fixed card
          height — the reserve there is generous enough. */}
      <div className="relative flex min-h-[max(520px,100svh)] flex-col overflow-hidden md:h-[calc(100svh-96px)] md:min-h-[720px] md:flex-row md:items-center md:rounded-[32px]">
        {/* Hero poster — server-preloaded via `preloadHero()` so the browser
            starts fetching the image before client hydration, claiming the
            LCP slot. The video lazy-attaches over it after page idle
            (skipped on Save-Data). */}
        {heroPoster}

        {/* Background video — source lazy-attached in useEffect above */}
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          className="absolute inset-0 z-0 h-full w-full object-cover"
        />

        {/* Top darkening overlay — sheet under the nav + hero title (top 0-25dvh) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[28svh]"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)",
          }}
        />

        {/* Bottom darkening overlay — matches Figma (node 37:50) */}
        <div
          aria-hidden
          className="absolute inset-0 z-[1]"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(56, 56, 56, 0) 11.965%, rgba(0, 0, 0, 0.6) 97.891%)",
          }}
        />

        {/* Main content — centered, vertical rhythm of 40px per Figma.
            Mobile uses tighter gaps for a more compact hero. The
            md:pb on the inner wrapper reserves space for the KPI strip
            absolutely positioned at the hero's bottom edge — without it
            the centered content (CTA buttons in particular) overlaps the
            KSPs on shorter desktop viewports. */}
        {/* max-md pt clears the fixed navbar (36px banner + 64px bar, so 64px
            inside this card) — 72px left the "Ahead membership" pill visually
            glued to the bar, hence 88px (AHD-10495). */}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 max-md:flex max-md:flex-1 max-md:flex-col max-md:justify-center max-md:pt-[88px] max-md:pb-6 md:pb-[160px] lg:px-20">
          <div className="mx-auto flex max-w-[780px] flex-col items-center gap-6 text-center md:gap-10">
            {/* mt/-mb nudges the pill down closer to the title without moving
                the H1 (the negative bottom margin offsets the column gap). */}
            <div className="mt-3 -mb-3 flex flex-wrap items-center justify-center gap-2">
              <NewMembershipPill label={t(mk.heroPill)} />
              {membershipComingSoon && (
                // self-center: the pill defaults to self-start, which pins it
                // to the top of this row next to the taller membership pill
                <ComingSoonPill
                  label={ut("comingSoon")}
                  variant="dark"
                  className="self-center"
                />
              )}
            </div>

            {/* Hero is paint-critical for LCP — keep it visible from the first
                frame. Only translate (no opacity) so Lighthouse / CrUX count
                the H1 as the LCP element instead of demoting to a body
                paragraph below. */}
            {/* ds-typo-ignore — hero H1 per Figma: 96px / 100% / −0.5px */}
            <h1 className="text-center text-[48px] leading-[52px] font-normal tracking-[-0.5px] text-white md:text-[96px] md:leading-[96px]">
              {t(mk.heroHeadingLine1)}
              <br />
              {t(mk.heroHeadingLine2)}
            </h1>

            <div>
              <p className="type-body-xl max-w-xl text-white">
                {t(mk.heroSubtitle)}
              </p>
            </div>

            <div className="max-md:mt-[10px]">
              {/* gap-y only shows on mobile, where the CTAs wrap to two lines;
                  desktop keeps them side-by-side at gap-x-[10px]. */}
              <div className="flex flex-wrap justify-center gap-x-[10px] gap-y-5">
                <BookingCta
                  forMembership
                  className={buttonVariants({
                    variant: "tonal",
                    className:
                      "h-[60px] bg-white px-8 text-neutral-900 hover:bg-white",
                  })}
                  trackingLabel="membership-hero-cta"
                  trackingLocation="membership"
                >
                  {t(mk.heroCtaPrimary)}
                  <ArrowRightIcon />
                </BookingCta>
                {/* Insurance-coverage CTA (CH-only — gated below). Inline link
                    on the dark hero: the heart pulses on hover and the underline
                    brightens to white; no link-wide background change. Reuses the
                    already-approved `checkInsuranceCoverage` string. Shown on
                    mobile + desktop. */}
                <CountryGate id="section:marketing.insurance-coverage-cta">
                  <Link
                    href={lp("/insurance")}
                    className="group inline-flex items-center gap-2 self-center px-4 py-2"
                  >
                    <HeartCheckPulsingIcon
                      size={22}
                      pulse
                      className="shrink-0 text-white/90"
                    />
                    <span className="type-body-md font-medium text-white/70 underline decoration-white/30 underline-offset-4 transition-colors duration-200 group-hover:text-white group-hover:decoration-white">
                      {ut("checkInsuranceCoverage")}
                    </span>
                  </Link>
                </CountryGate>
              </div>
            </div>
          </div>
        </div>

        {/* KPI strip — pinned inside the card on every breakpoint. Mobile
            uses 3 compact columns with vertical dividers and smaller text;
            desktop shows the larger title + label treatment. Holds for 1s
            after page load so the hero composition lands first, then the
            trust strip fades in underneath. Direct mount animation (delay
            anchored to page load, not scroll) so it plays even on short
            viewports where the strip sits above the fold. */}
        <div className="relative z-10 w-full pb-7 md:absolute md:inset-x-0 md:bottom-0 md:pb-8">
          <div className="mx-auto max-w-[860px] px-4 md:px-10">
            <div
              className={cn(
                "grid divide-x divide-white/15 md:gap-0",
                heroKpis.length === 3 ? "grid-cols-3" : "grid-cols-2"
              )}
            >
              {heroKpis.map((kpi, i) => (
                <div
                  key={kpi.valueKey}
                  className={cn(
                    "flex flex-col px-2 text-center md:text-left",
                    i > 0 && "md:pl-6",
                    i < heroKpis.length - 1 && "md:pr-6"
                  )}
                >
                  {/* ds-typo-ignore — compact KPI strip; mobile uses
                      sub-token sizes to keep three columns on one row. */}
                  <span className="text-[14px] leading-[15px] font-medium text-white/85 md:text-[18px] md:leading-[22px] md:text-white/75">
                    {t(kpi.valueKey)}
                  </span>
                  <span className="md:type-label-md mt-1.5 text-[10px] leading-[11px] tracking-[0.05em] text-white/40 uppercase md:mt-0.5 md:tracking-normal md:normal-case">
                    {t(kpi.labelKey)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   1b. Logo strip — trust logos below hero
   ──────────────────────────────────────────── */

type TrustLogoTag = "partnerWith" | "experienceAt" | "asSeenOn"

const TRUST_LOGO_TAG_LABEL_KEYS: Record<TrustLogoTag, string> = {
  partnerWith: mk.trustTagPartnerWith,
  experienceAt: mk.trustTagExperienceAt,
  asSeenOn: mk.trustTagAsSeenOn,
}

const TRUST_LOGOS: {
  src: string
  alt: string
  h: number
  tag: TrustLogoTag
}[] = [
  {
    src: blobMediaUrl("/images/logos/unilabs.svg"),
    alt: "Unilabs",
    h: 28,
    tag: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/siemens.svg"),
    alt: "Siemens Healthineers",
    h: 24,
    tag: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/oxford.svg"),
    alt: "University of Oxford",
    h: 28,
    tag: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/google.svg"),
    alt: "Google",
    h: 24,
    tag: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/tum.svg"),
    alt: "TUM",
    h: 28,
    tag: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/karolinska.svg"),
    alt: "Karolinska Institutet",
    h: 26,
    tag: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/quote.svg"),
    alt: "QUOTE",
    h: 24,
    tag: "asSeenOn",
  },
]

function LogoStrip() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <section className="pt-8 pb-12 md:pt-6 md:pb-24">
      {/* Mobile: slow auto-scrolling marquee on the white background so all
          7 logos read in one continuous line. Desktop: static centered row. */}
      <div className="md:hidden">
        <LogoMarquee
          items={TRUST_LOGOS.map((l) => ({ src: l.src, alt: l.alt, h: 20 }))}
          theme="light"
          duration={45}
          gapClassName="gap-10"
        />
      </div>
      <div className="mx-auto hidden max-w-[1440px] px-4 md:block md:px-10">
        <div className="flex items-center justify-center gap-x-12 lg:gap-x-16">
          {TRUST_LOGOS.map((logo) => (
            <div
              key={logo.alt}
              className="group relative flex flex-col items-center"
            >
              <span className="type-label-sm pointer-events-none absolute bottom-full mb-2 translate-y-1 whitespace-nowrap text-neutral-300 opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100">
                {t(TRUST_LOGO_TAG_LABEL_KEYS[logo.tag])}
              </span>
              <Image
                src={logo.src}
                alt={logo.alt}
                width={logo.h * 4}
                height={logo.h}
                className="opacity-55 grayscale transition-opacity duration-150 group-hover:opacity-80"
                style={{ height: logo.h * 1.3, width: "auto" }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   2. Why continuous tracking — narrative beat
   Prevention > reaction
   ──────────────────────────────────────────── */

const RECOGNITION_STATEMENT_KEYS = [
  mk.whyStatement1,
  mk.whyStatement2,
  mk.whyStatement3,
]

function WhyContinuous() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <section className="bg-background pt-40 pb-16 md:py-48">
      <div className="mx-auto max-w-[1200px] px-4 md:px-10">
        <div className="mx-auto max-w-[1040px] text-center">
          <div>
            <SectionLabel>{t(mk.whyLabel)}</SectionLabel>
          </div>
          <div>
            <h2 className="type-display-md text-neutral-900">
              {t(mk.whyHeadingLead)}{" "}
              <span className="text-bright-blue-600">
                {t(mk.whyHeadingAccent)}
              </span>
            </h2>
          </div>
        </div>

        <div>
          <ul className="mt-16 grid grid-cols-1 max-md:mx-auto max-md:max-w-[280px] max-md:divide-y max-md:divide-neutral-200/70 md:mt-36 md:grid-cols-3 md:divide-x md:divide-neutral-200">
            {RECOGNITION_STATEMENT_KEYS.map((key) => (
              <li key={key} className="py-6 text-center md:px-10 md:py-0">
                <p className="type-body-lg md:type-title-md font-medium max-md:text-neutral-600 md:font-normal md:text-neutral-500">
                  {t(key)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   3. What's Included — the anchor
   Platform framing, clickable IA cards, illustrations
   ──────────────────────────────────────────── */

/* Figma layout: heading left + 2-col grid right, then 4-col row below */

function Included() {
  return (
    <section id="whats-included" className="relative px-3 py-3 md:px-4 md:py-4">
      <div className="rounded-[24px] bg-white px-4 py-8 md:rounded-[32px] md:px-0 md:py-28">
        <div className="mx-auto max-w-[1440px] md:px-10">
          <IncludedFigmaGrid />
        </div>
      </div>
    </section>
  )
}

/* Deep-green check — DS status color #408c49, swapped in for GreenCheck
   in the membership-year bullet lists per Jana's spec. */
function DeepGreenCheck({
  size = 20,
  className,
}: {
  size?: number
  className?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7.5" fill="#408c49" />
      <path
        d="M5 8l2 2 4-4"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ────────────────────────────────────────────
   4b. Locations strip — compact reassurance that the membership
   ships in real Swiss cities. Sits between the timeline (what)
   and frequency picker (cost) so it answers the geographic
   objection right before the visitor evaluates pricing.

   Visual: static Switzerland outline (echoing the locations-page
   map style) sits behind a wrap of glass-pill city names. The
   pills are intentionally non-interactive — this section answers
   "where" at a glance rather than serving as a picker. The list
   itself is sourced from the cities CMS, deduped server-side.
   ──────────────────────────────────────────── */

// Coordinates per city. Drives the pin positions on the Leaflet
// terrain map; coming-soon entries share the same table so a single
// lookup powers both states.
const CITY_LAYOUT: Record<string, { lat: number; lng: number }> = {
  // Active blood-clinic cities
  zurich: { lat: 47.376, lng: 8.541 },
  bern: { lat: 46.948, lng: 7.447 },
  basel: { lat: 47.56, lng: 7.588 },
  horgen: { lat: 47.26, lng: 8.598 },
  zug: { lat: 47.166, lng: 8.515 },
  "st-gallen": { lat: 47.424, lng: 9.377 },
  winterthur: { lat: 47.5, lng: 8.724 },
  // Coming soon
  lausanne: { lat: 46.52, lng: 6.633 },
  geneva: { lat: 46.204, lng: 6.143 },
  montreux: { lat: 46.434, lng: 6.911 },
  schaffhausen: { lat: 47.697, lng: 8.633 },
}

// The Leaflet map is heavy and only needs to run client-side. Lazy
// load it so it doesn't bloat the membership page's main client
// chunk; ssr:false avoids the SSR-incompatible window/document
// access that react-leaflet's MapContainer makes at import time.
const MembershipLocationsMap = dynamic(
  () =>
    import("@/components/ui/membership-locations-map").then(
      (m) => m.MembershipLocationsMap
    ),
  { ssr: false }
)

// Slug order for coming-soon cities, paired positionally with the
// `locationsSoonCities` translation string. Keeping the slug list
// here lets the translation stay a simple human-edited comma list
// while still mapping each entry to its coordinate.
const SOON_SLUGS = ["montreux"]

// Total pins the map will render — mirrors the filter the map applies
// to drop entries without a `CITY_LAYOUT` coord. Surfaced on the strip
// as `data-pin-count` so the e2e test asserts against the live CMS-
// derived count instead of a hand-bumped constant.
function countMembershipPins(
  active: { slug: string }[],
  soon: { slug: string }[]
): number {
  return (
    active.filter((c) => CITY_LAYOUT[c.slug]).length +
    soon.filter((c) => CITY_LAYOUT[c.slug]).length
  )
}

// Renders the `#f4f4f4` tile + the Leaflet terrain map. Stretches
// to the parent grid cell's height (`h-full`) so it matches the
// neighbouring text column height — no more squashed aspect-locked
// silhouette.
function LocationsMap({
  active,
  soon,
}: {
  active: { slug: string; name: string }[]
  soon: { slug: string; name: string }[]
}) {
  const pins = [
    ...active.flatMap((c) => {
      const l = CITY_LAYOUT[c.slug]
      return l
        ? [
            {
              slug: c.slug,
              name: c.name,
              lat: l.lat,
              lng: l.lng,
              status: "active" as const,
            },
          ]
        : []
    }),
    ...soon.flatMap((c) => {
      const l = CITY_LAYOUT[c.slug]
      return l
        ? [
            {
              slug: c.slug,
              name: c.name,
              lat: l.lat,
              lng: l.lng,
              status: "soon" as const,
            },
          ]
        : []
    }),
  ]
  return (
    // Negative margins bleed past the card's md:p-14 padding on
    // top/bottom/left so the map sits closer to the card edges,
    // while leaving the right column at its original 56px padding.
    // 56 (card) − 32 (negative margin) = 24px gap to card edge.
    // The matching `h-[calc(100%+64px)]` adds back the 32+32 of
    // height the negative margins would otherwise eat — without it,
    // the tile only shifts up but doesn't grow, so its bottom edge
    // sits short of the right column's bottom by 32px.
    <div className="bg-background relative h-full min-h-[240px] overflow-hidden rounded-[20px] md:-mt-8 md:-mb-8 md:-ml-8 md:h-[calc(100%+64px)] md:min-h-[360px]">
      <MembershipLocationsMap pins={pins} />
    </div>
  )
}

function LocationsStrip({
  cities,
}: {
  cities: { slug: string; name: string }[]
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const lp = useLocalePath()
  if (cities.length === 0) return null
  const soon = t(mk.locationsSoonCities)
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name, i) => ({ slug: SOON_SLUGS[i] ?? name.toLowerCase(), name }))
  const pinCount = countMembershipPins(cities, soon)
  return (
    <section
      id="locations-strip"
      data-pin-count={pinCount}
      className="bg-background relative py-3 md:py-4"
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="relative overflow-hidden rounded-[24px] bg-white px-6 py-10 md:rounded-[32px] md:px-14 md:py-14">
          <div className="relative grid grid-cols-1 items-stretch gap-10 md:grid-cols-2 md:gap-16">
            <div className="h-full">
              <LocationsMap active={cities} soon={soon} />
            </div>

            <div>
              <div className="flex flex-col gap-6 text-left md:gap-7">
                <div className="flex flex-col gap-2">
                  <SectionLabel>{t(mk.locationsLabel)}</SectionLabel>
                  <h2 className="type-display-md text-neutral-900">
                    {t(mk.locationsHeading)}
                  </h2>
                  <p className="type-body-lg mt-2 max-w-[460px] text-neutral-600 md:mt-0">
                    {t(mk.locationsDescription)}
                  </p>
                </div>
                {/* Two columns: column 1 has the "Available now" header
                    plus the first 5 active cities; column 2 continues the
                    active list with the remaining cities, then drops the
                    "Coming soon" header and the soon cities. Splitting
                    this way keeps the section height roughly balanced
                    instead of having a very tall left column and a short
                    right one. */}
                {(() => {
                  // Split active cities so the right column has ~enough room
                  // for the "Coming soon" header + soon list without the
                  // section getting taller. With 8 active + 3 soon today,
                  // 6/2 keeps the columns visually even.
                  const SPLIT = 6
                  const activeCol1 = cities.slice(0, SPLIT)
                  const activeCol2 = cities.slice(SPLIT)
                  const activeItem = (c: { slug: string; name: string }) => (
                    <li
                      key={c.slug}
                      className="type-body-lg inline-flex items-center gap-2.5 text-neutral-900"
                    >
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 shrink-0 rounded-full bg-neutral-900"
                      />
                      {c.name}
                    </li>
                  )
                  const soonItem = (c: { slug: string; name: string }) => (
                    <li
                      key={c.slug}
                      className="type-body-lg inline-flex items-center gap-2.5 text-neutral-500"
                    >
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 shrink-0 rounded-full border-[1.5px] border-neutral-400 bg-white"
                      />
                      {c.name}
                    </li>
                  )
                  return (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      <div className="flex flex-col gap-3">
                        <h3 className="type-label-lg text-neutral-500 normal-case">
                          {t(mk.locationsLegendActive)}
                        </h3>
                        <ul className="flex flex-col gap-1.5">
                          {activeCol1.map(activeItem)}
                        </ul>
                      </div>
                      <div className="flex flex-col gap-3">
                        <ul className="flex flex-col gap-1.5">
                          {activeCol2.map(activeItem)}
                        </ul>
                        {soon.length > 0 && (
                          <>
                            <h3 className="type-label-lg text-neutral-500 normal-case">
                              {t(mk.locationsSoonLabel)}
                            </h3>
                            <ul className="flex flex-col gap-1.5">
                              {soon.map(soonItem)}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })()}
                {/* Quiet line bridging the membership-only locations
                    to the broader Ahead network — context, not a
                    competing CTA. */}
                <p className="type-body-md max-w-[460px] text-neutral-500">
                  {t(mk.locationsCheckupNote)}
                </p>
                <div>
                  <a
                    href={lp("/locations")}
                    className={cn(buttonVariants({ variant: "outline" }))}
                  >
                    {t(mk.locationsCta)}
                    <ArrowRightIcon className="ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   5. Cadence selector — "How often we check in"
   Helper text + comparison row + simplified bottom
   ──────────────────────────────────────────── */

function FrequencyBadge({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [isMultiline, setIsMultiline] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => {
      const lh = parseFloat(getComputedStyle(el).lineHeight)
      if (!Number.isFinite(lh) || lh === 0) return
      setIsMultiline(el.offsetHeight > lh * 1.5)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <span
      ref={ref}
      className={cn(
        "type-label-md bg-light-green-200 -ml-1.5 inline-flex self-start px-2.5 py-1 text-neutral-900 normal-case",
        isMultiline ? "rounded-[4px]" : "rounded-full"
      )}
    >
      {text}
    </span>
  )
}

function FrequencyPicker() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  // [TODO: confirm DE pricing] — `FREQUENCY_OPTIONS.price` figures are CHF.
  // On EUR markets we don't yet have authoritative annual prices, so suppress
  // the price line entirely there rather than mislabel CHF amounts as EUR.
  const { currency } = useCountry()
  const showPriceLine = currency === "CHF"
  const scrollRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  // Track scroll edges so the mobile carousel nav can disable its buttons.
  // Skipped on sm+ where the layout becomes a static grid (no scroll).
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      // When the layout collapses to the desktop grid the element is no
      // longer a scroll container; bail to keep edges in their initial state.
      if (el.scrollWidth <= el.clientWidth + 1) {
        setAtStart(true)
        setAtEnd(true)
        return
      }
      setAtStart(el.scrollLeft <= 1)
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
    }
    update()
    el.addEventListener("scroll", update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", update)
      ro.disconnect()
    }
  }, [])

  // Snap to the next/previous card boundary using each card's measured
  // offset so the small inter-card gap doesn't drift the snap point.
  const scrollToCard = (dir: "left" | "right") => {
    const el = scrollRef.current
    if (!el) return
    const cards = Array.from(el.children) as HTMLElement[]
    if (cards.length === 0) return
    const containerRect = el.getBoundingClientRect()
    const padLeft = parseFloat(getComputedStyle(el).paddingLeft) || 0
    const current = el.scrollLeft
    const targets = cards.map((c) => {
      const r = c.getBoundingClientRect()
      return Math.max(0, r.left - containerRect.left + current - padLeft)
    })
    let next: number | undefined
    if (dir === "right") {
      next = targets.find((t) => t > current + 5)
    } else {
      for (let i = targets.length - 1; i >= 0; i--) {
        if (targets[i] < current - 5) {
          next = targets[i]
          break
        }
      }
      if (next === undefined) next = 0
    }
    if (next === undefined) return
    el.scrollTo({ left: next, behavior: "smooth" })
  }

  return (
    <section id="cadence" className="relative py-16 md:py-24">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center gap-6 px-6 md:px-16">
        {/* Centered header */}
        <div>
          <div className="mx-auto flex flex-col items-center gap-2 text-center">
            <p className="type-label-md text-neutral-900">
              {t(mk.frequencyLabel)}
            </p>
            <h2 className="type-display-md text-neutral-900">
              {t(mk.frequencyHeading)}
            </h2>
            <p className="type-body-md mt-2 max-w-[540px] text-neutral-600">
              {t(mk.frequencyDescription)}
            </p>
          </div>
        </div>

        {/* Three columns of comparison content. Mobile = horizontal snap
            carousel of subtly-tinted cards on the white page. Desktop = 3-col
            grid with thin vertical dividers between columns — reads as a spec
            table, not as clickable cards. */}
        <div
          ref={scrollRef}
          className="-mx-6 mt-4 flex w-[calc(100%+48px)] [touch-action:pan-x_pan-y] snap-x snap-mandatory scroll-pl-4 gap-4 overflow-x-auto overflow-y-visible overscroll-x-contain pr-4 pl-4 [scrollbar-width:none] sm:mx-0 sm:grid sm:w-full sm:snap-none sm:grid-cols-3 sm:gap-0 sm:overflow-visible sm:px-0 md:mt-12 [&::-webkit-scrollbar]:hidden"
        >
          {FREQUENCY_OPTIONS.map((o, i) => (
            <div
              key={i}
              className={cn(
                "w-[78%] shrink-0 snap-start sm:w-auto sm:shrink",
                i > 0 &&
                  "sm:border-l sm:border-neutral-900/15 sm:pl-8 lg:pl-10",
                i < FREQUENCY_OPTIONS.length - 1 && "sm:pr-8 lg:pr-10"
              )}
            >
              <div className="flex h-full flex-col rounded-2xl bg-white px-6 py-6 sm:rounded-none sm:bg-transparent sm:p-0">
                {/* Header group: icons + title + badge, 12px gaps */}
                <div className="flex flex-col gap-2 md:gap-3">
                  {/* Lab icons */}
                  <div className="flex h-7 items-center">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/icons/labs.svg" alt="" className="h-7 w-auto" />
                    {i >= 1 && (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img
                        src="/icons/add.svg"
                        alt=""
                        className="mx-1.5 h-3.5 w-3.5 shrink-0"
                      />
                    )}
                    {i >= 1 && (
                      <div className="flex items-center -space-x-0.5">
                        {Array.from({ length: i === 1 ? 1 : 3 }).map((_, j) => (
                          /* eslint-disable-next-line @next/next/no-img-element */
                          <img
                            key={j}
                            src="/icons/labs-60.svg"
                            alt=""
                            className="h-7 w-auto"
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <h3 className="type-title-lg text-neutral-900">
                    {t(o.nameKey)}
                  </h3>

                  {/* Badge */}
                  <FrequencyBadge text={t(o.testsKey)} />
                </div>

                {/* Description — flex-1 so price pins to bottom */}
                <p className="type-body-md mt-4 flex-1 text-neutral-900 md:mt-6">
                  {t(o.descKey)}
                </p>

                {/* Price */}
                {showPriceLine && (
                  <p className="mt-4 text-neutral-900 md:mt-6">
                    <span className="type-body-md">{currency} </span>
                    <span className="text-[28px] leading-[36px] font-medium">
                      {o.price}
                    </span>
                    <span className="type-body-sm">
                      {t(mk.frequencyPerYearSuffix)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile-only carousel nav — desktop renders the 3-up grid with
            no horizontal scroll, so the chevrons would be no-ops there. */}
        <CarouselNav
          onPrev={() => scrollToCard("left")}
          onNext={() => scrollToCard("right")}
          atStart={atStart}
          atEnd={atEnd}
          className="-mt-2 w-full sm:hidden"
        />

        {/* Savings line — sits just above the CTA so the "vs. booking
            separately" benefit is the last thing the visitor reads before
            converting. Subtitle (under the heading) keeps its original
            program-statement copy. */}
        {showPriceLine && (
          <div className="md:mt-8">
            <p className="type-body-md text-center font-medium text-neutral-900">
              {t(mk.frequencySavings)}
            </p>
          </div>
        )}

        {/* CTA — primary booking button + insurance-coverage CTA. The latter
            is CH-only (renders nothing in other countries). */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3">
          <BookingCta
            forMembership
            className={buttonVariants({ variant: "primary", size: "lg" })}
            trackingLabel="membership-frequency-cta"
            trackingLocation="membership"
          >
            {t(mk.frequencyCta)}
            <ArrowRightIcon className="ml-2" />
          </BookingCta>
          {/* hover:bg-transparent drops the md variant's link-wide hover fill —
              hover keeps only the underline + heart pulse. */}
          <InsuranceCoverageCta
            size="md"
            icon="heart-check-pulse"
            className="hover:bg-transparent"
          />
        </div>

        <div>
          <p className="type-body-md max-w-[325px] text-center leading-[22px] text-neutral-500">
            {t(mk.frequencyBillingNote)}
          </p>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   6. What we test for — blood test detail
   Repositioned after timeline
   ──────────────────────────────────────────── */

function WhatWeTestFor({ biomarkers }: { biomarkers: CMSBiomarkerListItem[] }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const lp = useLocalePath()
  const [panel, setPanel] = useState<"baseline" | "midyear">("baseline")

  const baselineTotal = biomarkers.filter((b) =>
    b.packages.includes("blood")
  ).length
  const followupTotal = biomarkers.filter((b) =>
    b.packages.includes("blood-followup")
  ).length

  const isBaseline = panel === "baseline"
  const packages = isBaseline ? ["blood"] : ["blood-followup"]

  return (
    <BiomarkerDiseaseExplorer
      key={panel}
      id="what-we-test"
      packages={packages}
      seedBiomarkers={biomarkers}
      tabsAllowed={["biomarkers"]}
      eyebrow={t(mk.testLabel)}
      biomarkersTab={{
        heading: t(mk.testHeading),
        subheading: t(mk.testDescription),
        cta: {
          label: t(mk.testSeeAll),
          href: lp("/biomarkers/blood"),
          variant: "outline",
        },
      }}
      panelTabs={[
        {
          value: "baseline",
          label: `${t(mk.testTabBaseline)} (${baselineTotal})`,
          image: "/icons/lab-color.svg",
        },
        {
          value: "midyear",
          label: `${t(mk.testTabFollowup)} (${followupTotal})`,
          image: "/icons/lab-color-60.svg",
        },
      ]}
      activePanel={panel}
      onPanelChange={(v) => setPanel(v as "baseline" | "midyear")}
      panelNote={
        !isBaseline && (
          <div className="w-full">
            <div className="bg-deep-green-100 w-full rounded-2xl p-4 text-center">
              <p className="type-body-md text-deep-green-800">
                {t(mk.testFollowupNote)}
              </p>
            </div>
          </div>
        )
      }
    />
  )
}

/* ────────────────────────────────────────────
   7. Action plan — concrete example
   With dual CTAs (sample report + quiz)
   ──────────────────────────────────────────── */

/* Figma 194:4211 — full Personal Plan card rebuild. Sits as a glass overlay
   on the physician-patient photo in the ActionPlanPreview section. */
function PlanChip({
  icon,
  children,
  active,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
  active?: boolean
}) {
  if (active) {
    return (
      <div className="bg-deep-blue-950 inline-flex shrink-0 items-center justify-center rounded-[34.752px] px-[13.901px] py-[6.082px]">
        <span className="text-[10.5px] leading-[1.1] font-medium whitespace-nowrap text-white">
          {children}
        </span>
      </div>
    )
  }
  return (
    <div className="inline-flex shrink-0 items-center justify-center gap-[5.213px] rounded-[34.752px] bg-white/[0.55] py-[6.082px] pr-[13.901px] pl-[10.426px]">
      {icon && (
        <span className="text-foreground flex size-[13.901px] shrink-0 items-center justify-center">
          {icon}
        </span>
      )}
      <span className="text-foreground text-[10.5px] leading-[1.1] font-normal whitespace-nowrap">
        {children}
      </span>
    </div>
  )
}

function PlanCardChip({
  icon,
  children,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="inline-flex shrink-0 items-center justify-center gap-[3.475px] rounded-[40px] bg-white/[0.34] py-[3.475px] pr-[8.688px] pl-[5.213px]">
      {icon && (
        <span className="text-foreground flex size-[13.901px] shrink-0 items-center justify-center">
          {icon}
        </span>
      )}
      <span className="text-foreground text-[9px] leading-none font-normal whitespace-nowrap">
        {children}
      </span>
    </div>
  )
}

function PlanThumbnail({
  color,
  children,
}: {
  color: string
  children: React.ReactNode
}) {
  return (
    <div
      className="flex size-[44px] shrink-0 items-center justify-center rounded-[10px] text-white"
      style={{ backgroundColor: color }}
    >
      {children}
    </div>
  )
}

function ImpactBullseyeIcon({
  className,
  size = 12,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle
        cx="6"
        cy="6"
        r="5"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
      <circle
        cx="6"
        cy="6"
        r="2.2"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  )
}

function CalendarDayIcon({
  className,
  size = 12,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect
        x="1.5"
        y="2.5"
        width="9"
        height="8"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M1.5 4.5H10.5M4 1.5V3M8 1.5V3"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ActionCardRow({
  thumb,
  title,
  description,
  chips,
}: {
  thumb: React.ReactNode
  title: string
  description: string
  chips: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-[10px] overflow-hidden rounded-[14px] bg-white/30 p-[10px]">
      <div className="flex items-center gap-[8px]">
        {thumb}
        <div className="flex min-w-0 flex-1 flex-col gap-[1px]">
          <p className="text-foreground text-[13px] leading-[1.15] font-medium">
            {title}
          </p>
          <p className="text-foreground text-[10px] leading-[1.3]">
            {description}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-[5px]">{chips}</div>
    </div>
  )
}

function PersonalPlanCard() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div className="border-neutral-00 mx-auto flex w-full max-w-[282px] flex-col gap-[12px] overflow-hidden rounded-[15.065px] border-[0.628px] bg-white/60 px-[14px] pt-[32px] pb-[32px] backdrop-blur-[4px]">
      {/* Header */}
      <div className="text-foreground flex flex-col gap-[5.213px]">
        <p className="text-[22px] leading-[28px] font-medium">
          {t(mk.overlayPlanTitle)}
        </p>
      </div>

      {/* Filter chips — horizontally scrollable, clipped to card */}
      <div className="-mx-[15.065px] overflow-x-auto px-[15.065px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex w-max items-center gap-[6.95px]">
          <PlanChip active>{t(mk.overlayPlanFilterAll)}</PlanChip>
          <PlanChip icon={<FollowUpIcon size={13} />}>
            {t(mk.overlayPlanFilterFollowups)}
          </PlanChip>
          <PlanChip icon={<ExerciseIcon size={13} />}>
            {t(mk.overlayPlanFilterExercise)}
          </PlanChip>
          <PlanChip icon={<NutritionIcon size={13} />}>
            {t(mk.overlayPlanFilterNutrition)}
          </PlanChip>
          <PlanChip icon={<LifestyleIcon size={13} />}>
            {t(mk.overlayPlanFilterLifestyle)}
          </PlanChip>
        </div>
      </div>

      {/* Actions section */}
      <div className="flex flex-col gap-[7.819px]">
        <div className="flex flex-col gap-[10.426px]">
          <ActionCardRow
            thumb={
              <PlanThumbnail color="rgba(255, 255, 255, 0.55)">
                <SupplementsIcon size={20} className="text-black" />
              </PlanThumbnail>
            }
            title={t(mk.overlayPlanCalciumTitle)}
            description={t(mk.overlayPlanCalciumDesc)}
            chips={
              <>
                <PlanCardChip icon={<ImpactBullseyeIcon />}>
                  {t(mk.overlayPlanChipImpactAreas2)}
                </PlanCardChip>
                <PlanCardChip icon={<NutritionIcon size={12} />}>
                  {t(mk.overlayPlanChipNutrition)}
                </PlanCardChip>
                <PlanCardChip icon={<CalendarDayIcon />}>
                  {t(mk.overlayPlanChipDaily)}
                </PlanCardChip>
              </>
            }
          />
          <ActionCardRow
            thumb={
              <PlanThumbnail color="rgba(255, 255, 255, 0.55)">
                <ExerciseIcon size={20} className="text-neutral-950" />
              </PlanThumbnail>
            }
            title={t(mk.overlayPlanExerciseTitle)}
            description={t(mk.overlayPlanExerciseDesc)}
            chips={
              <>
                <PlanCardChip icon={<ImpactBullseyeIcon />}>
                  {t(mk.overlayPlanChipImpactAreas3)}
                </PlanCardChip>
                <PlanCardChip icon={<ExerciseIcon size={12} />}>
                  {t(mk.overlayPlanChipExercise)}
                </PlanCardChip>
                <PlanCardChip icon={<CalendarDayIcon />}>
                  {t(mk.overlayPlanChipWeekly)}
                </PlanCardChip>
              </>
            }
          />
        </div>
      </div>

      {/* Follow-ups section */}
      <div className="flex flex-col gap-[7.819px]">
        <div className="flex flex-col gap-[10px] rounded-[14px] bg-white/30 p-[10px]">
          <div className="flex items-center gap-[8px]">
            <PlanThumbnail color="rgba(255, 255, 255, 0.55)">
              <Report2Icon size={22} className="text-neutral-950" />
            </PlanThumbnail>
            <div className="flex min-w-0 flex-1 flex-col gap-[1px]">
              <p className="text-foreground text-[13px] leading-[1.15] font-medium">
                {t(mk.overlayPlanPotassiumTitle)}
              </p>
              <p className="text-foreground text-[10px] leading-[1.3]">
                {t(mk.overlayPlanPotassiumDesc)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-[5px]">
            <PlanCardChip icon={<ImpactBullseyeIcon />}>
              {t(mk.overlayPlanChipKidney)}
            </PlanCardChip>
            <PlanCardChip icon={<CalendarDayIcon />}>
              {t(mk.overlayPlanChipDueWeeks)}
            </PlanCardChip>
          </div>
          <div className="flex items-center gap-[6px]">
            <button
              type="button"
              className="text-foreground flex-1 rounded-[40px] bg-white/[0.55] px-[10px] py-[6px] text-[10px] font-medium"
            >
              {t(mk.overlayPlanBtnViewDetails)}
            </button>
            <button
              type="button"
              className="bg-foreground/80 flex-1 rounded-[40px] px-[10px] py-[6px] text-[10px] font-medium text-white"
            >
              {t(mk.overlayPlanBtnBookTest)}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ActionPlanPreview() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const lp = useLocalePath()
  const planBulletKeys = [mk.planBullet1, mk.planBullet2, mk.planBullet3]
  return (
    <section id="action-plan" className="relative px-3 py-6 md:px-4 md:py-4">
      <div className="rounded-[24px] bg-white py-16 md:rounded-[32px] md:py-24">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <div className="max-w-[416px]">
                <SectionLabel>{t(mk.planLabel)}</SectionLabel>
                <h2 className="mb-6 text-[30px] leading-[38px] font-normal tracking-[-0.25px] text-neutral-900 md:text-[48px] md:leading-[56px] md:tracking-[-0.5px]">
                  {t(mk.planHeading)}
                </h2>
                <p className="type-body-lg mb-6 text-neutral-600">
                  {t(mk.planDescription)}
                </p>
                <ul className="mb-8 flex flex-col gap-3">
                  {planBulletKeys.map((key) => (
                    <li
                      key={key}
                      className="type-body-md flex items-start gap-3 text-neutral-900"
                    >
                      <DeepGreenCheck size={20} className="mt-0.5 shrink-0" />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={lp("/report")}
                  className={buttonVariants({
                    variant: "tonal",
                    className: "group",
                  })}
                >
                  {t(mk.planCta)}
                  <ArrowRightIcon className="ml-1 transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                </a>
              </div>
            </div>

            <div>
              <div className="relative aspect-[1193/1142] w-full max-w-[520px] overflow-hidden rounded-[24px]">
                <Image
                  src={blobMediaUrl("/images/membership/action-plan-bg.jpg")}
                  alt={t(mk.planImageAlt)}
                  fill
                  sizes="(min-width: 768px) 520px, 100vw"
                  className="object-cover"
                />
                <m.div
                  initial={{ y: 20 }}
                  whileInView={{ y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.9,
                    ease: [0.16, 1, 0.3, 1],
                    delay: 0.15,
                  }}
                  className="absolute top-[34px] right-[34px] left-[34px] md:top-[11%] md:right-auto md:left-[26px] md:w-fit"
                >
                  <PersonalPlanCard />
                </m.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   8. Member pricing — hybrid: narrative + catalog
   "Everything Ahead offers, at member prices"
   ──────────────────────────────────────────── */

/* Reusable shared catalog table — uses the same row/column treatment as
   the membership-vs-single-test comparison table for visual consistency. */
export function MemberPricingCatalog({
  id,
  dense = false,
  initialPrices = null,
}: {
  id?: string
  dense?: boolean
  /** Server-loaded live prices; otherwise fetched client-side so the catalog
   *  works when rendered without a server pricing pass. */
  initialPrices?: PriceData | null
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  // [TODO: confirm DE pricing] — literal fallbacks are CHF. EUR markets don't
  // have authoritative member prices yet, so don't render the CHF figure with
  // an EUR label. We still show the row structure (the savings story stands on
  // its own); price cells display a dash placeholder.
  const { code: country, currency } = useCountry()
  const showFigures = currency === "CHF"
  const cellPad = dense ? "py-3" : "py-3 sm:py-4"
  const [prices, setPrices] = useState<PriceData | null>(initialPrices)
  useEffect(() => {
    if (initialPrices) {
      setPrices(initialPrices)
      return
    }
    fetchPrices(country).then(setPrices)
  }, [country, initialPrices])
  return (
    <div id={id} className="mx-auto max-w-[820px]">
      <table className="w-full table-fixed border-collapse">
        <caption className="sr-only">{t(mk.memberPricingCaption)}</caption>
        <colgroup>
          <col />
          <col className="w-[68px] sm:w-[120px]" />
          <col className="w-[72px] sm:w-[120px]" />
          <col className="w-[64px] sm:w-[100px]" />
        </colgroup>
        <thead>
          <tr>
            <th
              scope="col"
              className="type-label-md py-3 text-left font-normal tracking-[0.5px] text-neutral-400 uppercase sm:py-4"
            >
              {t(mk.memberPricingColService)}
            </th>
            <th
              scope="col"
              className="type-label-md py-3 text-right font-normal tracking-[0.5px] text-neutral-400 uppercase sm:py-4"
            >
              {t(mk.memberPricingColRegular)}
            </th>
            <th
              scope="col"
              className="type-label-md py-3 text-right font-normal tracking-[0.5px] text-neutral-500 uppercase sm:py-4"
            >
              {t(mk.memberPricingColMember)}
            </th>
            <th
              scope="col"
              className="type-label-md py-3 text-right font-normal tracking-[0.5px] text-neutral-400 uppercase sm:py-4"
            >
              {t(mk.memberPricingColSave)}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200 border-y border-neutral-200">
          {MEMBER_PRICING_ITEMS.map((item, i) => {
            const row = resolveMemberRow(item, prices, country)
            return (
              <tr key={i}>
                <th
                  scope="row"
                  className={cn("pr-3 text-left align-middle sm:pr-4", cellPad)}
                >
                  <span className="type-body-sm sm:type-body-md font-medium text-neutral-900">
                    {t(item.productKey)}
                  </span>
                </th>
                <td className={cn("text-right align-middle", cellPad)}>
                  <span className="type-body-sm sm:type-body-md text-neutral-400 line-through">
                    {showFigures ? row.regular : "—"}
                  </span>
                </td>
                <td className={cn("text-right align-middle", cellPad)}>
                  <span className="type-body-sm sm:type-body-md font-medium text-neutral-900">
                    {showFigures ? row.member : "—"}
                  </span>
                </td>
                <td className={cn("text-right align-middle", cellPad)}>
                  <span className="type-body-sm text-deep-green-500 font-medium">
                    {showFigures ? row.save : "—"}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ── Layout 6: Editorial — row of service add-on cards ── */
type EditorialBlock = {
  titleKey: string
  subtitleKey: string
  /** Price-map key the "you save" delta resolves from live (regular − member).
   *  Omitted for full-body MRI, which has no live member price. */
  priceKey?: string
  saved: string
  image: string
  path: string
}

const EDITORIAL_BLOCKS: EditorialBlock[] = [
  {
    titleKey: mk.editorialMriTitle,
    subtitleKey: mk.editorialMriSubtitle,
    saved: "259",
    image: blobMediaUrl("/images/homepage/service-mri.jpeg"),
    path: "/tests/full-body-mri",
  },
  {
    titleKey: mk.editorialBVitaminsTitle,
    subtitleKey: mk.editorialBVitaminsSubtitle,
    priceKey: "addonBVitamins",
    saved: "10",
    image: blobMediaUrl("/images/homepage/service-bvitamins.jpeg"),
    path: "/tests/essential-b-vitamins-test",
  },
  {
    titleKey: mk.editorialHormoneTitle,
    subtitleKey: mk.editorialHormoneSubtitle,
    priceKey: "addonHormoneWomen",
    saved: "30",
    image: blobMediaUrl("/images/homepage/service-hormone-women.jpeg"),
    path: "/tests/hormone-blood-test-women",
  },
  {
    titleKey: mk.editorialOmegaTitle,
    subtitleKey: mk.editorialOmegaSubtitle,
    priceKey: "addonOmega",
    saved: "10",
    image: blobMediaUrl("/images/homepage/service-omega.jpeg"),
    path: "/tests/omega-blood-test",
  },
]

/* ── Member pricing & personalization ── */
function MemberPricing({
  servicesConfig = [],
  initialPrices = null,
}: {
  servicesConfig?: CmsServiceConfig[]
  initialPrices?: PriceData | null
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const ut = useTranslations("ui")
  const [showCatalog, setShowCatalog] = useState(false)
  const { code: country, currency } = useCountry()
  // Live Stripe prices drive the per-card "you save" delta and the catalog's
  // regular/member/save figures; literal fallbacks cover first paint and EUR.
  const [prices, setPrices] = useState<PriceData | null>(initialPrices)
  useEffect(() => {
    if (initialPrices) {
      setPrices(initialPrices)
      return
    }
    fetchPrices(country).then(setPrices)
  }, [country, initialPrices])

  // Build ServicesCarousel items from the membership's editorial blocks,
  // using each block's per-card savings amount as the overlay (replacing
  // the homepage's biomarker chip with a "You save CHF X" pill). The
  // descriptions come from the homepage's own service-card copy
  // (`ui.serviceCard*Desc`) so the tone matches the rest of the
  // ServicesCarousel surface area on the site.
  const HOMEPAGE_DESC_KEY: Record<string, string> = {
    "/tests/full-body-mri": "serviceCardMriDesc",
    "/tests/essential-b-vitamins-test": "serviceCardBVitaminsDesc",
    "/tests/hormone-blood-test-women": "serviceCardHormoneWomenDesc",
    "/tests/omega-blood-test": "serviceCardOmegaDesc",
  }
  const editorialItems: ServiceCardData[] = EDITORIAL_BLOCKS.map((b) => {
    const descKey = HOMEPAGE_DESC_KEY[b.path]
    return {
      title: t(b.titleKey),
      description: descKey ? ut(descKey as never) : t(b.subtitleKey),
      image: b.image,
      path: b.path,
      // Suppress the per-card savings overlay on EUR markets — we don't yet
      // have authoritative discounted figures, and stamping "EUR <CHF-number>"
      // would be misleading.
      biomarker:
        currency === "CHF"
          ? {
              type: "savings",
              label: t(mk.memberPricingCardSave),
              amount: `CHF ${feedSaveAmount(prices, b.priceKey, country) ?? b.saved}`,
            }
          : undefined,
    }
  })
  // CMS control plane is the sole driver of carousel gating + order; empty
  // config = ungated passthrough (loader fires Sentry — fail-loud).
  const carouselItems = applyServicesConfig(
    editorialItems,
    servicesConfig,
    country
  )

  return (
    <section id="member-pricing" className="relative bg-neutral-50 py-16">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div>
          <SectionHeader
            label={t(mk.memberPricingLabel)}
            title={t(mk.memberPricingHeading)}
            description={t(mk.memberPricingDescription)}
          />
        </div>
      </div>

      {/* Edge-to-edge carousel — uses the same component the homepage
          renders for the "It's time to get the full picture" section, with
          a custom savings overlay in place of the biomarker chip. The
          "See all member prices" toggle lives in the carousel's toolbar
          row so it sits inline with the prev/next chevrons. */}
      <div className="mt-8">
        <ServicesCarousel
          items={carouselItems}
          learnMoreLabel={ut("learnMore")}
          toolbarLeading={
            <button
              type="button"
              onClick={() => setShowCatalog((v) => !v)}
              className="type-title-md inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 px-6 py-3 text-neutral-700 transition-all hover:bg-neutral-50"
              aria-expanded={showCatalog}
              aria-controls="member-pricing-catalog"
            >
              {showCatalog
                ? t(mk.memberPricingHideAll)
                : t(mk.memberPricingShowAll)}
              <ChevronDownSmIcon
                className={cn(
                  "transition-transform",
                  showCatalog && "rotate-180"
                )}
              />
            </button>
          }
        />
      </div>

      {showCatalog && (
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="mt-6">
            <MemberPricingCatalog
              id="member-pricing-catalog"
              initialPrices={prices}
            />
            <p className="type-body-sm mt-3 text-center text-neutral-400">
              {t(mk.memberPricingFootnote)}
            </p>
          </div>
        </div>
      )}
    </section>
  )
}

/* ────────────────────────────────────────────
   Membership vs. single test — feature comparison
   Resolves the "why not just buy a single test?" objection
   ──────────────────────────────────────────── */

type CompareValue =
  | { kind: "check" }
  | { kind: "x" }
  | {
      kind: "text"
      textKey: string
      noteKey?: string
      tone?: "pos" | "neutral"
    }

const COMPARE_ROWS: {
  featureKey: string
  member: CompareValue
  single: CompareValue
  tagKey?: string
}[] = [
  // Differentiators first: these are why you'd pick membership
  {
    featureKey: mk.compareRow1Feature,
    member: {
      kind: "text",
      textKey: mk.compareRow1MemberText,
      noteKey: mk.compareRow1MemberNote,
      tone: "pos",
    },
    single: {
      kind: "text",
      textKey: mk.compareRow1SingleText,
      tone: "neutral",
    },
  },
  {
    featureKey: mk.compareRow2Feature,
    member: { kind: "check" },
    single: { kind: "x" },
  },
  {
    featureKey: mk.compareRow3Feature,
    member: { kind: "check" },
    single: { kind: "x" },
  },
  {
    featureKey: mk.compareRow4Feature,
    member: { kind: "check" },
    single: { kind: "x" },
  },
  {
    featureKey: mk.compareRow5Feature,
    member: { kind: "check" },
    single: { kind: "x" },
  },
  // Shared baseline: you get these either way
  {
    featureKey: mk.compareRow6Feature,
    member: { kind: "check" },
    single: { kind: "check" },
  },
  {
    featureKey: mk.compareRow7Feature,
    member: { kind: "check" },
    single: { kind: "check" },
  },
  // Forward-looking: one honest line, not three
  {
    featureKey: mk.compareRow8Feature,
    member: { kind: "check" },
    single: { kind: "x" },
    tagKey: mk.compareRow8Tag,
  },
]

function CompareCell({ value }: { value: CompareValue }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  if (value.kind === "check") {
    return (
      <span
        role="img"
        className="text-deep-green-500 inline-flex"
        aria-label={t(mk.compareAriaIncluded)}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="11"
            cy="11"
            r="9.5"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M6.5 11.5 9.5 14.5 15.5 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    )
  }
  if (value.kind === "x") {
    return (
      <span
        role="img"
        className="inline-flex text-neutral-300"
        aria-label={t(mk.compareAriaNotIncluded)}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="11"
            cy="11"
            r="9.5"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M7.5 7.5 14.5 14.5M14.5 7.5 7.5 14.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </span>
    )
  }
  const tone = value.tone === "pos" ? "text-deep-green-500" : "text-neutral-400"
  return (
    <span
      className={cn("inline-flex flex-col items-center leading-tight", tone)}
    >
      <span className="type-body-md font-semibold">{t(value.textKey)}</span>
      {value.noteKey && (
        <span className="type-body-sm mt-0.5 hidden font-normal opacity-80 sm:inline">
          {t(value.noteKey)}
        </span>
      )}
    </span>
  )
}

/* Decorative overlay that frames the "selected" Member column.
   Sits at z-10 so its green border covers row dividers at intersections. */
const MEMBER_COL_CLASS = "right-[96px] sm:right-[180px] w-[96px] sm:w-[180px]"

function SelectedColumnHighlight() {
  return (
    <>
      <div
        aria-hidden
        className={cn(
          "border-deep-green-500 pointer-events-none absolute inset-y-0 z-10 rounded-[12px] border-2 sm:rounded-[16px]",
          MEMBER_COL_CLASS
        )}
      />
      <div
        aria-hidden
        className={cn(
          "bg-deep-green-500 pointer-events-none absolute top-0 z-10 h-[41px] rounded-t-[12px] sm:h-[49px] sm:rounded-t-[16px]",
          MEMBER_COL_CLASS
        )}
      />
    </>
  )
}

function ComparisonTable() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div className="relative">
      <SelectedColumnHighlight />

      <table className="w-full table-fixed border-collapse">
        <caption className="sr-only">{t(mk.compareCaption)}</caption>
        <colgroup>
          <col />
          <col className="w-[96px] sm:w-[180px]" />
          <col className="w-[96px] sm:w-[180px]" />
        </colgroup>
        <thead>
          <tr>
            <th
              scope="col"
              className="type-label-md py-3 text-left font-normal tracking-[0.5px] text-neutral-400 uppercase sm:py-4"
            >
              {t(mk.compareColFeature)}
            </th>
            <th
              scope="col"
              className="type-label-md relative z-20 py-3 font-normal tracking-[0.5px] text-white uppercase sm:py-4"
            >
              {t(mk.compareColMembers)}
            </th>
            <th
              scope="col"
              className="type-label-md py-3 font-normal tracking-[0.5px] text-neutral-400 uppercase sm:py-4"
            >
              {t(mk.compareColSingle)}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200 border-y border-neutral-200">
          {COMPARE_ROWS.map((row) => (
            <tr key={row.featureKey}>
              <th
                scope="row"
                className="py-3 pr-3 text-left align-middle sm:py-4 sm:pr-4"
              >
                <span className="type-body-sm sm:type-body-md inline-flex flex-wrap items-center gap-2 font-medium text-neutral-900">
                  {t(row.featureKey)}
                  {row.tagKey && (
                    <span className="type-label-md text-deep-green-500 tracking-[0.5px] uppercase">
                      {t(row.tagKey)}
                    </span>
                  )}
                </span>
              </th>
              <td className="py-3 text-center align-middle sm:py-4">
                <CompareCell value={row.member} />
              </td>
              <td className="py-3 text-center align-middle sm:py-4">
                <CompareCell value={row.single} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MembershipVsSingleTest() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <section
      id="membership-vs-single"
      className="relative px-3 py-3 md:px-4 md:py-4"
    >
      <div className="rounded-[24px] bg-white/70 py-16 md:rounded-[32px] md:py-24">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 md:gap-12 md:px-10 lg:grid-cols-[1fr_1.75fr]">
          <div>
            <div className="flex flex-col items-start gap-4">
              <p className="type-label-md text-neutral-500">
                {t(mk.compareLabel)}
              </p>
              <h2 className="type-display-sm text-neutral-900">
                {t(mk.compareHeading)}
              </h2>
              <p className="type-body-lg text-neutral-900">
                {t(mk.compareDescription)}
              </p>
              <BookingCta
                forMembership
                className={buttonVariants({
                  variant: "primary",
                  className: "group mt-2",
                })}
                trackingLabel="membership-vs-single-cta"
                trackingLocation="membership"
              >
                {t(mk.heroCtaPrimary)}
                <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              </BookingCta>
            </div>
          </div>

          <div>
            <ComparisonTable />
          </div>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   9. Clinical team — compact strip
   ──────────────────────────────────────────── */

function ClinicalTeam() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const lp = useLocalePath()
  // Hidden on DE for now — the Swiss-physician "experts behind your check-up"
  // module isn't shown on the German membership page. AHD-6680.
  // country-gate-ignore: DE checkup-phase content hide, not a page gate
  const { code: country } = useCountry()
  if (country === "de") return null
  return (
    <section
      id="clinical-team"
      className="lazy-section bg-white px-3 py-16 md:px-4 md:py-24"
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[1fr_1.75fr] md:gap-12">
          {/* Left column: heading + supporting copy + CTA */}
          <div className="h-full">
            <div className="flex h-full flex-col">
              <SectionLabel>{t(mk.teamLabel)}</SectionLabel>
              <h2 className="type-display-md text-neutral-900">
                {t(mk.teamHeading)}
              </h2>
              <p className="type-body-lg mt-4 max-w-[420px] text-neutral-600">
                {t(mk.teamDescription)}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
                <Image
                  src={blobMediaUrl("/images/logos/oxford.svg")}
                  alt="University of Oxford"
                  width={112}
                  height={28}
                  className="h-7 w-auto opacity-70 grayscale"
                />
                <Image
                  src={blobMediaUrl("/images/logos/karolinska.svg")}
                  alt="Karolinska Institutet"
                  width={68}
                  height={28}
                  className="h-7 w-auto opacity-70 grayscale"
                />
                <Image
                  src={blobMediaUrl("/images/logos/st-gallen.svg")}
                  alt="University of St.Gallen"
                  width={139}
                  height={28}
                  className="h-7 w-auto opacity-70 grayscale"
                />
              </div>
              <a
                href={`${lp("/about")}#advisory-board`}
                className={cn(
                  buttonVariants({
                    variant: "outline",
                    shape: "round",
                    size: "default",
                  }),
                  "group mt-8 self-start border-neutral-200 md:mt-auto"
                )}
              >
                {t(mk.teamCta)}
                <ArrowRightIcon
                  size={20}
                  className="transition-transform duration-200 ease-out group-hover:translate-x-1"
                />
              </a>
            </div>
          </div>

          {/* Right column: doctors photo grid with hover-reveal bio */}
          <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-3">
            {EXPERTS.map((doctor) => (
              <div key={doctor.name}>
                <div className="group relative aspect-[326/397] overflow-hidden rounded-[12px]">
                  <Image
                    src={doctor.photo}
                    alt={doctor.name}
                    fill
                    sizes="(max-width: 1280px) 50vw, 33vw"
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:opacity-40" />
                  <div className="absolute right-2 bottom-2 left-2 z-10 flex flex-col gap-1">
                    <div className="rounded-[8px] bg-white/[0.77] px-3 py-2.5 backdrop-blur-sm">
                      <p className="type-title-sm sm:type-title-md leading-snug text-neutral-900">
                        {doctor.name}
                      </p>
                    </div>
                    <div className="max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:max-h-40 group-hover:opacity-100">
                      <div className="rounded-[8px] bg-white/[0.24] px-3 py-2.5">
                        <p className="type-body-md leading-snug text-white">
                          {t(doctor.titleKey)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   10. FAQ
   ──────────────────────────────────────────── */

function MembershipFaq({
  initialFaqs,
}: {
  initialFaqs: { question: string; answer: string }[]
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const lp = useLocalePath()
  const localePrefix = useLocalePrefix()

  // CMS-curated content (managed in Payload, `pages` tag = "membership").
  // Answers are HTML — `sanitizeHtml` handles them.
  const items = initialFaqs

  return (
    <section className="px-3 py-16 md:px-4 md:py-24">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 md:grid-cols-[1fr_1.75fr] md:px-10">
        {/* Left column */}
        <div>
          <div>
            <h2 className="text-4xl font-normal text-neutral-900 md:text-5xl">
              {t(mk.faqHeading)}
            </h2>
            <a
              href={lp("/faq")}
              className={buttonVariants({
                variant: "text",
                size: "sm",
                className: "group mt-6 gap-2 !px-0",
              })}
            >
              {t(mk.faqLink)}
              <ArrowRightIcon
                size={16}
                className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>

        {/* Right column */}
        <div className="divide-y divide-neutral-200">
          {items.map((item, index) => {
            const isOpen = openFaq === index
            const panelId = `membership-faq-panel-${index}`
            return (
              <div key={`${item.question}-${index}`}>
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-between py-4 text-left"
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className="text-base text-neutral-700">
                    {item.question}
                  </span>
                  <ChevronDownIcon
                    className={cn(
                      "ml-4 flex-shrink-0 text-neutral-400 transition-transform duration-200",
                      isOpen && "rotate-180"
                    )}
                  />
                </button>
                {/*
                  Always-mounted answer panel (collapsed via height:0 instead
                  of conditional render) so Googlebot, screen readers, and
                  Cmd-F can find the text. The previous AnimatePresence +
                  `{openFaq === index && …}` pattern only rendered the answer
                  on click.
                */}
                <m.div
                  id={panelId}
                  role="region"
                  aria-hidden={!isOpen}
                  inert={!isOpen}
                  initial={false}
                  animate={
                    isOpen
                      ? { height: "auto", opacity: 1 }
                      : { height: 0, opacity: 0 }
                  }
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="overflow-hidden"
                  style={{ height: 0, opacity: 0 }}
                >
                  <div
                    className="[&_a]:text-aubergine-700 pb-4 text-sm text-neutral-500 [&_a]:underline [&_a]:underline-offset-2 [&_li]:mb-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 last:[&_p]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(item.answer, localePrefix),
                    }}
                  />
                </m.div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   12. Final CTA — banner
   "Start your membership"
   ──────────────────────────────────────────── */

const CTA_FEATURE_KEYS = [
  mk.finalFeature1,
  mk.finalFeature2,
  mk.finalFeature3,
  mk.finalFeature4,
  mk.finalFeature5,
  mk.finalFeature6,
]

const CTA_PILLARS = [
  { labelKey: mk.finalPillar1, icon: "stethoscope" },
  { labelKey: mk.finalPillar2, icon: "impact" },
  { labelKey: mk.finalPillar3, icon: "person" },
] as const

function FinalCta() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const { currency } = useCountry()
  const showPriceLine = currency === "CHF"
  return (
    <section
      className="relative flex w-full items-center justify-center overflow-hidden px-4 py-10 md:py-16"
      style={{ minHeight: "clamp(500px, 46vw, 667px)" }}
    >
      <Image
        src={blobMediaUrl("/images/bg.jpeg")}
        alt=""
        aria-hidden="true"
        fill
        priority
        fetchPriority="high"
        sizes="100vw"
        className="object-cover"
      />

      {/* Centered frosted card */}
      <div className="relative z-10 w-full">
        <div
          className="mx-auto w-full max-w-[780px] rounded-2xl p-5 md:p-10"
          style={{
            border: "1px solid rgba(255, 255, 255, 0.16)",
            background: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(29.15px)",
            WebkitBackdropFilter: "blur(29.15px)",
          }}
        >
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-stretch md:gap-0">
            {/* Left — branding + price + CTA */}
            <div className="flex w-full shrink-0 flex-col md:w-[303px] md:border-r md:border-black/10 md:pr-8">
              {/* Logo + heading: side-by-side on mobile, stacked on md+ */}
              <div className="mb-3 flex items-center gap-3 md:block">
                <div className="relative size-12 shrink-0 overflow-hidden rounded-full md:mb-3 md:size-[80px]">
                  <Image
                    src="/icons/membership-bg.png"
                    alt=""
                    width={80}
                    height={80}
                    className="object-cover"
                  />
                  <Image
                    src="/icons/ahead-logo.svg"
                    alt="Ahead"
                    width={36}
                    height={36}
                    className="absolute inset-0 m-auto h-6 w-6 md:h-9 md:w-9"
                  />
                </div>

                <h2 className="type-title-lg md:type-display-sm text-neutral-900">
                  {t(mk.finalHeading)}
                </h2>
              </div>

              {showPriceLine && (
                <p className="mb-6 text-neutral-900">
                  <span className="type-body-md">{t(mk.finalPricePrefix)}</span>
                  <span className="text-[28px] leading-[36px] font-medium">
                    299
                  </span>
                  <span className="type-body-sm">{t(mk.finalPriceSuffix)}</span>
                </p>
              )}

              <BookingCta
                forMembership
                className={cn(
                  buttonVariants({ variant: "primary" }),
                  "w-full md:mt-auto md:w-auto"
                )}
                trackingLabel="membership-final-cta"
                trackingLocation="membership"
              >
                {t(mk.heroCtaPrimary)}
                <ArrowRightIcon className="ml-2" />
              </BookingCta>
            </div>

            {/* Right — pillars + checklist */}
            <div className="flex flex-1 flex-col gap-2 md:gap-4 md:pl-8">
              {/* Three pillars */}
              <m.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                variants={{
                  hidden: {},
                  visible: { transition: { staggerChildren: 0.08 } },
                }}
                className="flex gap-2"
              >
                {CTA_PILLARS.map((p) => (
                  <m.div
                    key={p.labelKey}
                    variants={{
                      hidden: { opacity: 0, y: 8 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: expoOut as unknown as number[],
                        },
                      },
                    }}
                    className="flex flex-1 flex-col items-center gap-1 px-1 py-0 text-center md:py-2"
                  >
                    <div className="flex size-10 items-center justify-center text-neutral-500">
                      {p.icon === "stethoscope" && (
                        <SwissShieldIcon size={28} />
                      )}
                      {p.icon === "impact" && <ImpactAreaIcon size={28} />}
                      {p.icon === "person" && <FollowUpIcon size={28} />}
                    </div>
                    <p className="text-[12px] leading-[1.2] font-medium text-neutral-900">
                      {t(p.labelKey)}
                    </p>
                  </m.div>
                ))}
              </m.div>

              {/* Checklist */}
              <div className="flex flex-col">
                {CTA_FEATURE_KEYS.map((key) => (
                  <div key={key} className="flex h-10 items-center gap-2 px-2">
                    <GreenCheck width={16} height={16} className="shrink-0" />
                    <span className="type-body-md text-neutral-900">
                      {t(key)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────
   Page
   ──────────────────────────────────────────── */

export default function MembershipClient({
  biomarkers,
  cities,
  initialFaqs,
  heroPoster,
  initialTestimonials = [],
  servicesConfig = [],
  initialPrices = null,
}: {
  biomarkers: CMSBiomarkerListItem[]
  cities: { slug: string; name: string }[]
  /** FAQs from Payload (`pages` tag = "membership"). */
  initialFaqs: { question: string; answer: string }[]
  heroPoster: React.ReactNode
  initialTestimonials?: Testimonial[]
  /** CMS `services` control plane (per-country status + order), joined by slug
   *  onto the member-pricing carousel. Empty = ungated render + a Sentry alert
   *  from the loader (fail-loud). */
  servicesConfig?: CmsServiceConfig[]
  /** Live product + member prices from Stripe (get-products). Drives the
   *  member-pricing catalog + per-card savings; null falls back to literals
   *  and a client-side fetch. */
  initialPrices?: PriceData | null
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div className="bg-background min-h-screen">
      <Hero heroPoster={heroPoster} />
      <LogoStrip />
      <WhyContinuous />
      <Included />
      <MembershipYearLazy />
      <CountryGate id="section:membership.locations-strip">
        <LocationsStrip cities={cities} />
      </CountryGate>
      <FrequencyPicker />
      <WhatWeTestFor biomarkers={biomarkers} />
      <ActionPlanPreview />
      <TestimonialsSection
        heading={t(mk.testimonialsHeading)}
        description={t(mk.testimonialsDescription)}
        cmsTestimonials={initialTestimonials}
      />
      <MemberPricing
        servicesConfig={servicesConfig}
        initialPrices={initialPrices}
      />
      <MembershipVsSingleTest />
      <ClinicalTeam />
      <MembershipFaq initialFaqs={initialFaqs} />
      <FinalCta />
    </div>
  )
}
